import {
  BADGE_VARIANT_BLUE_SOFT,
  BADGE_VARIANT_DARK_GRAY,
  BADGE_VARIANT_GRAY_DISABLED,
  BADGE_VARIANT_GREEN,
  BADGE_VARIANT_IN_REVIEW,
  BADGE_VARIANT_LIGTH_BLUE_SOFT,
  BADGE_VARIANT_ORANGE_SOFT,
  BADGE_VARIANT_RED,
  BADGE_VARIANT_YELLOW,
  Variant,
} from '@components/badge/src/badge.type';
import { DragHandleRenderProps } from '@components/sortable-list';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import {
  ADMISSION_STATUS_HIDDEN,
  ADMISSION_STATUS_HIDDEN_BY_TRIGGER,
  ADMISSION_STATUS_IN_REVIEW,
  ADMISSION_STATUS_ON_SALE,
  ADMISSION_STATUS_SALES_CLOSED,
  ADMISSION_STATUS_SOLD_OUT,
} from '@domain/admission';
import { AdmissionGroup } from '@domain/admission-group';
import { Channel } from '@domain/channel';
import { PassHolders } from '@domain/passes';
import { TFunction } from 'i18next';

export const ADMISSION_STATUS_RESOLD = 'resold';
export const PRE_REGISTRATION_STATUS_CLOSED = 'closed';
export const PRE_REGISTRATION_STATUS_ENABLED = 'enabled';
export const PRE_REGISTRATION_STATUS_DISABLED = 'disabled';

export type AdmissionCardStatus =
  | typeof ADMISSION_STATUS_HIDDEN
  | typeof ADMISSION_STATUS_HIDDEN_BY_TRIGGER
  | typeof ADMISSION_STATUS_ON_SALE
  | typeof ADMISSION_STATUS_SALES_CLOSED
  | typeof ADMISSION_STATUS_SOLD_OUT
  | typeof ADMISSION_STATUS_IN_REVIEW
  | typeof ADMISSION_STATUS_RESOLD
  | typeof PRE_REGISTRATION_STATUS_DISABLED
  | typeof PRE_REGISTRATION_STATUS_ENABLED
  | typeof PRE_REGISTRATION_STATUS_CLOSED;

export const ADMISSION_CARD_TAG_ACCESS_CODE = 'accessCode';
export const ADMISSION_CARD_TAG_LIMITED = 'limited';
export const ADMISSION_CARD_TAG_IMPORT_ONLY = 'importOnly';
export const ADMISSION_CARD_TAG_EXPIRED = 'expired';
export const ADMISSION_CARD_TAG_ACTIVE = 'active';

export type AdmissionCardTag =
  | typeof ADMISSION_CARD_TAG_ACCESS_CODE
  | typeof ADMISSION_CARD_TAG_IMPORT_ONLY
  | typeof ADMISSION_CARD_TAG_LIMITED
  | typeof ADMISSION_CARD_TAG_EXPIRED
  | typeof ADMISSION_CARD_TAG_ACTIVE;

export interface Props {
  id: string;
  timezone: string;
  quantity: number | null;
  soldItems: number;
  currency: string;
  amount: number;
  onlinePrice?: number;
  ddpPrice?: number;
  isPrivate: boolean;
  isPublished: boolean;
  needsUserVerified: boolean;
  salesEnds: number;
  status?: AdmissionCardStatus;
  tags: AdmissionCardTag[];
  onClick?: () => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDeletePass?: (id: string) => void;
  onDuplicate?: () => void;
  onUnassign?: (id: string) => void;
  showSubmenuIcon?: boolean;
  name?: string;
  description?: string | null;
  color?: string | null;
  defaultChannel?: Omit<Channel, 'checked' | 'value'>;
  guestCapacity?: number;
  isPreset?: boolean;
  isPastEvent?: boolean;
  deleteIsUnassign?: boolean;
  checked?: boolean;
  isSelectActive?: boolean;
  isMultiSelectActive?: boolean;
  onChange?: (value: boolean) => void;
  eventId?: string;
  isInReviewStatus?: boolean;
  accessValidity?: string;
  isDragActive?: boolean;
  isInDragOverlay?: boolean;
  passHolders?: PassHolders;
  isActive?: boolean;
  onToggleClick?: ({ isToggleActive, passId }: { isToggleActive: boolean; passId: string }) => void;
  onClickViewHolders?: () => void;
  onClickAddHolder?: () => void;
  isPassCard?: boolean;
  /**
   * Optional drag handle props from SortableList.
   * When provided, AdmissionCard will use these instead of internal useSortable.
   * This allows the component to work with the new SortableList architecture.
   */
  dragHandle?: DragHandleRenderProps;
  sectionGroups?: AdmissionGroup[];
  sectionId?: string;
  isGroupCard?: boolean;
  groupId?: string;
  admissionTypeLabel?: string;
  isDeleting?: boolean;
  isCreating?: boolean;
  isEditing?: boolean;
}

export interface AdmissionCardInfoProps {
  id: string;
  tags: AdmissionCardTag[];
  soldItems: number;
  quantity: number | null;
  cardOpacity: string;
  checked?: boolean;
  status?: AdmissionCardStatus;
  listeners?: SyntheticListenerMap;
  accessValidity?: string;
  guestCapacity?: number;
  attributes?: DraggableAttributes;
  isDragActive?: boolean;
  isSelectActive?: boolean;
  isMultiSelectActive?: boolean;
  onChange?: (e: boolean) => void;
  color?: string | null;
  name?: string;
  description?: string | null;
  isDateVisible: boolean;
  onlinePrice?: number;
  ddpPrice?: number;
  formattedSalesEndsDate?: string;
  formattedOnlinePrice?: string;
  formatedDdpPrice?: string;
}

export interface AdmissionCardBadgesProps {
  badgeVariant: Variant | '';
  statusTranslated: string;
  status?: AdmissionCardStatus;
  tags: AdmissionCardTag[];
  accessValidity?: string;
  guestCapacity?: number;
  cardOpacity: string;
  soldItems: number;
  quantity: number | null;
  t: TFunction;
}

export const STATUS_VARIANT = {
  [ADMISSION_STATUS_ON_SALE]: BADGE_VARIANT_GREEN,
  [ADMISSION_STATUS_HIDDEN]: BADGE_VARIANT_DARK_GRAY,
  [ADMISSION_STATUS_SALES_CLOSED]: BADGE_VARIANT_YELLOW,
  [ADMISSION_STATUS_SOLD_OUT]: BADGE_VARIANT_RED,
  [ADMISSION_STATUS_HIDDEN_BY_TRIGGER]: BADGE_VARIANT_DARK_GRAY,
  [ADMISSION_STATUS_IN_REVIEW]: BADGE_VARIANT_IN_REVIEW,
  [ADMISSION_STATUS_RESOLD]: BADGE_VARIANT_BLUE_SOFT,
  [PRE_REGISTRATION_STATUS_CLOSED]: BADGE_VARIANT_RED,
  [PRE_REGISTRATION_STATUS_DISABLED]: BADGE_VARIANT_RED,
  [PRE_REGISTRATION_STATUS_ENABLED]: BADGE_VARIANT_GREEN,
};

export const STATUS_TRANSLATION_KEYS = {
  [ADMISSION_STATUS_ON_SALE]: 'common:admissionStatusOnSale',
  [ADMISSION_STATUS_HIDDEN]: 'common:admissionStatusHidden',
  [ADMISSION_STATUS_SALES_CLOSED]: 'common:admissionStatusSalesEnded',
  [ADMISSION_STATUS_SOLD_OUT]: 'common:admissionStatusSoldOut',
  [ADMISSION_STATUS_HIDDEN_BY_TRIGGER]: 'common:admissionStatusHiddenByTrigger',
  [ADMISSION_STATUS_IN_REVIEW]: 'common:admissionStatusInReview',
  [ADMISSION_STATUS_RESOLD]: 'common:admissionStatusResold',
  [PRE_REGISTRATION_STATUS_CLOSED]: 'common:preRegistrationStatusClosed',
  [PRE_REGISTRATION_STATUS_ENABLED]: 'common:preRegistrationStatusEnabled',
  [PRE_REGISTRATION_STATUS_DISABLED]: 'common:preRegistrationStatusDisabled',
};

export const TAG_VARIANT = {
  [ADMISSION_CARD_TAG_ACCESS_CODE]: BADGE_VARIANT_ORANGE_SOFT,
  [ADMISSION_CARD_TAG_LIMITED]: BADGE_VARIANT_BLUE_SOFT,
  [ADMISSION_CARD_TAG_IMPORT_ONLY]: BADGE_VARIANT_BLUE_SOFT,
  // Pass cards only
  [ADMISSION_CARD_TAG_EXPIRED]: BADGE_VARIANT_GRAY_DISABLED,
  [ADMISSION_CARD_TAG_ACTIVE]: BADGE_VARIANT_LIGTH_BLUE_SOFT,
};

export const TAG_TRANSLATION_KEYS = {
  [ADMISSION_CARD_TAG_ACCESS_CODE]: 'common:admissionTagAccessCode',
  [ADMISSION_CARD_TAG_LIMITED]: 'common:admissionTagLimited',
  [ADMISSION_CARD_TAG_IMPORT_ONLY]: 'offers:admissionTicketIsPrivateImportOnly',
  // Pass cards only
  [ADMISSION_CARD_TAG_EXPIRED]: 'passes:statusExpired',
  [ADMISSION_CARD_TAG_ACTIVE]: 'passes:statusActive',
};
